import { supabasePlatformProvider } from '../../integrations/supabase/platformProvider'
import type { CampaignStatus } from '../../domain/campaign/status'
import {
  createWorkspaceOperationContext,
  createWorkspaceContext,
  type WorkspaceOperationContext,
  type WorkspaceContext,
  type WorkspaceStatus,
} from '../../domain/workspace/lifecycle'
import { mapProviderError, type AppError } from './errors'

export type { AppError, AppErrorCode } from './errors'
export { mapProviderError } from './errors'

export type AppResult<T> = { data: T | null; error: AppError | null }
export type AuthUser = { id: string; email?: string | null }
export type AuthSession = { user: AuthUser }

type ProviderLikeResult<T = unknown> = { data: T | null; error: { message: string; code?: string | null } | null }

function safe<T>(result: ProviderLikeResult<T>): AppResult<T> {
  return { data: result.data, error: mapProviderError(result.error) }
}

async function execute<T>(operation: PromiseLike<ProviderLikeResult<T>>): Promise<AppResult<T>> {
  try {
    return safe(await operation)
  } catch {
    return { data: null, error: { code: 'UNAVAILABLE', message: 'Không thể hoàn tất yêu cầu. Vui lòng thử lại.' } }
  }
}

function requiredReason(reason: string): string {
  const normalized = reason.trim()
  if (normalized.length < 2 || normalized.length > 1000) throw new Error('reason must contain 2-1000 characters')
  return normalized
}

function transitionWorkspace(context: WorkspaceContext, targetStatus: WorkspaceStatus, reason: string) {
  const trusted = createWorkspaceContext(context)
  return execute(supabasePlatformProvider.workspace.transition({
    workspaceId: trusted.workspaceId,
    targetStatus,
    purpose: trusted.purpose,
    reason: requiredReason(reason),
    correlationId: trusted.correlationId,
  }))
}

export const platformApi = {
  auth: {
    async getSession(): Promise<AppResult<AuthSession>> {
      const result = await supabasePlatformProvider.auth.getSession()
      return { data: (result.data.session as unknown as AuthSession | null), error: mapProviderError(result.error) }
    },
    onSessionChange(callback: (session: AuthSession | null) => void) {
      return supabasePlatformProvider.auth.onSessionChange((session) => callback(session as AuthSession | null))
    },
    async signIn(email: string, password: string): Promise<AppResult<AuthSession>> {
      const result = await supabasePlatformProvider.auth.signIn(email, password)
      return { data: (result.data.session as unknown as AuthSession | null), error: mapProviderError(result.error) }
    },
    async signUp(input: { email: string; password: string; fullName: string; emailRedirectTo: string }): Promise<AppResult<AuthSession>> {
      const result = await supabasePlatformProvider.auth.signUp(input)
      return { data: (result.data.session as unknown as AuthSession | null), error: mapProviderError(result.error) }
    },
    async signOut(): Promise<AppResult<null>> {
      const result = await supabasePlatformProvider.auth.signOut()
      return { data: null, error: mapProviderError(result.error) }
    },
  },

  workspace: {
    async listMemberships(userId: string) { return execute(supabasePlatformProvider.workspace.listMemberships(userId)) },
    async bootstrapTenant(name: string, slug: string) { return execute(supabasePlatformProvider.workspace.bootstrapTenant(name, slug)) },
    async create(context: WorkspaceOperationContext, input: {
      name: string
      slug: string
      locale: string
      timezone: string
      branding: Record<string, unknown>
      reason: string
    }) {
      const trusted = createWorkspaceOperationContext(context)
      return execute(supabasePlatformProvider.workspace.create({
        ...input,
        reason: requiredReason(input.reason),
        purpose: trusted.purpose,
        correlationId: trusted.correlationId,
      }))
    },
    async updateSettings(context: WorkspaceContext, input: {
      name: string
      locale: string
      timezone: string
      branding: Record<string, unknown>
      reason: string
    }) {
      const trusted = createWorkspaceContext(context)
      return execute(supabasePlatformProvider.workspace.updateSettings({
        ...input,
        reason: requiredReason(input.reason),
        workspaceId: trusted.workspaceId,
        purpose: trusted.purpose,
        correlationId: trusted.correlationId,
      }))
    },
    async transition(context: WorkspaceContext, targetStatus: WorkspaceStatus, reason: string) {
      return transitionWorkspace(context, targetStatus, reason)
    },
    async suspend(context: WorkspaceContext, reason: string) {
      return transitionWorkspace(context, 'suspended', reason)
    },
    async activate(context: WorkspaceContext, reason: string) {
      return transitionWorkspace(context, 'active', reason)
    },
    async archive(context: WorkspaceContext, reason: string) {
      return transitionWorkspace(context, 'archived', reason)
    },
  },

  campaign: {
    async listForOperator(tenantId: string) { return execute(supabasePlatformProvider.campaign.listForOperator(tenantId)) },
    async listForClient(tenantId: string) { return execute(supabasePlatformProvider.campaign.listForClient(tenantId)) },
    async create(input: { tenantId: string; name: string; brandName: string; description: string | null; createdBy: string }) {
      return execute(supabasePlatformProvider.campaign.create(input))
    },
    async transition(id: string, status: CampaignStatus, reason: string) {
      return execute(supabasePlatformProvider.campaign.transition(id, status, reason))
    },
  },

  creatorOperations: {
    async listCreators(campaignId: string) { return execute(supabasePlatformProvider.creatorOperations.listCreators(campaignId)) },
    async listDeliverables(campaignId: string) { return execute(supabasePlatformProvider.creatorOperations.listDeliverables(campaignId)) },
    async listTasks(campaignId: string) { return execute(supabasePlatformProvider.creatorOperations.listTasks(campaignId)) },
    async listOpenExceptions(campaignId: string) { return execute(supabasePlatformProvider.creatorOperations.listOpenExceptions(campaignId)) },
    async importCreators(campaignId: string, rows: unknown[]) { return execute(supabasePlatformProvider.creatorOperations.importCreators(campaignId, rows)) },
    async createDeliverable(input: { tenantId: string; campaignId: string; name: string; type: string; defaultDueAt: string | null; requiresDraft: boolean; createdBy: string }) {
      return execute(supabasePlatformProvider.creatorOperations.createDeliverable(input))
    },
    async assignDeliverable(deliverableId: string) { return execute(supabasePlatformProvider.creatorOperations.assignDeliverable(deliverableId)) },
    async resolveException(input: { id: string; note: string; resolvedBy: string; resolvedAt: string }) {
      return execute(supabasePlatformProvider.creatorOperations.resolveException(input))
    },
  },

  taskLinks: {
    async list(campaignId: string) { return execute(supabasePlatformProvider.taskLinks.list(campaignId)) },
    async issue(taskId: string, expiresHours: number) { return execute(supabasePlatformProvider.taskLinks.issue(taskId, expiresHours)) },
    async revoke(linkId: string, reason: string) { return execute(supabasePlatformProvider.taskLinks.revoke(linkId, reason)) },
    async reopen(input: { taskId: string; phase: 'draft' | 'publication'; reason: string; dueAt: string | null }) {
      return execute(supabasePlatformProvider.taskLinks.reopen(input))
    },
  },

  creatorPortal: {
    async getTask(token: string) { return execute(supabasePlatformProvider.creatorPortal.getTask(token)) },
    async acknowledge(token: string) { return execute(supabasePlatformProvider.creatorPortal.acknowledge(token)) },
    async submit(input: { token: string; kind: 'link' | 'text'; contentValue: string; creatorNote: string | null }) {
      return execute(supabasePlatformProvider.creatorPortal.submit(input))
    },
    async publish(input: { token: string; postUrl: string; creatorNote: string | null }) {
      return execute(supabasePlatformProvider.creatorPortal.publish(input))
    },
  },

  approvals: {
    async listSubmissions(campaignId: string) { return execute(supabasePlatformProvider.approvals.listSubmissions(campaignId)) },
    async listOpenExceptions(campaignId: string, types: string[]) { return execute(supabasePlatformProvider.approvals.listOpenExceptions(campaignId, types)) },
    async updateException(id: string, patch: Record<string, unknown>) { return execute(supabasePlatformProvider.approvals.updateException(id, patch)) },
    async createException(input: Record<string, unknown>) { return execute(supabasePlatformProvider.approvals.createException(input)) },
    async reviewSubmission(input: { submissionId: string; versionNumber: number; decision: string; comment: string | null; revisionDueAt: string | null }) {
      return execute(supabasePlatformProvider.approvals.reviewSubmission(input))
    },
    async findOpenInvalidLinkException(campaignId: string, taskId: string) {
      return execute(supabasePlatformProvider.approvals.findOpenInvalidLinkException(campaignId, taskId))
    },
  },

  clientDashboard: {
    async get(campaignId: string) { return execute(supabasePlatformProvider.clientDashboard.get(campaignId)) },
  },
}
